export interface User {
  username: string;
  roles: Role[]; // All roles the user has
  activeRole: Role; // Currently active/selected role
  centerName: string | null; // Currently active center (when CENTER role is active)
  centerNames: string[]; // All centers the user has access to
}
export interface Role {
  name: string;
  enumName: string;
  description: string;
  permissions: Permission[];
}

// All possible permissions
export enum Permission {
  // Toegang tot dashboard
  DASHBOARD_READ = 'dashboard:read', // Read dashboard data
  DASHBOARD_CYCLE = 'dashboard:cycle', // Cycle dashboard data
  DASHBOARD_MATCHRUN = 'dashboard:matchrun', // Match running dashboard data
  DASHBOARD_OWN_CENTER_PATIENTS = 'dashboard:own_center_patients', // View own center patients (count)
  DASHBOARD_ALL_PATIENTS = 'dashboard:all_patients', // View all center patients
  DASHBOARD_NOTIFICATIONS = 'dashboard:notifications', // View dashboard notifications
  DASHBOARD_NEWSITEMS = 'dashboard:newsitems', // View dashboard news items

  PARTICIPANTS_ADD_INCLUDING_ALTRUISTS = 'participants:add_including_altruist', // Opvoeren deelnemers, inclusief altruïsten
  PARTICIPANTS_VIEW_OWN_CENTER = 'participants:view_own_center', // bekijken gegevens eigen deelnemer
  PARTICIPANS_VIEW_WITH_WARNINGS = 'participants:view_with_warnings', // View participants with warnings
  PARTICIPANTS_VIEW_ALL = 'participants:view_all', // bekijken gegevens alle deelnemers
  PARTICIPANTS_VIEW_HISTORY = 'participants:view_history', // bekijken historie deelnemer
  PARTICIPANTS_EDIT = 'participants:edit', // aanpasen gegevens deelnemer
  PARTICIPANTS_SET_TRANSPLANTED = 'participants:set_transplanted', // vastleggen transplantatie individuele deelnemer
  PARTICIPANTS_INACTIVATE = 'participants:inactive', // inactief zetten deelnemer
  PARTICIPANTS_IMPORT_ENIS = 'participants:import_enis', // verrijken gegevens deelnemers door import gegevens

  ENISSYNC_VIEW_LOGS = 'enissync:view_logs', // bekijken ENISSYNC logs

  PAIRS_LINK_PARTICIPANTS = 'pairs:link_participants', // koppelen deelnemers tot paren
  PAIRS_VIEW_OWN_CENTER = 'pairs:view_own_center', // bekijken gegevens eigen paren
  PAIRS_VIEW_ALL = 'pairs:view_all', // bekijken gegevens alle paren
  PAIRS_VIEW_HISTORY = 'pairs:view_history', // bekijken historie paren
  PAIRS_EDIT = 'pairs:edit', // aanpassen paren,
  PAIRS_INACTIVATE = 'pairs:inactive', // paren op inactief zetten
  PAIRS_ADD_TO_CYCLE = 'pairs:add_to_cycle', // paren aan een cyclus toevoegen
  PAIRS_REMOVE_FROM_CYCLE = 'pairs:remove_from_cycle', // paren uit een cyclus halen

  PRIVILEGES_ADD = 'privileges_add', //invoeren privileges
  PRIVILEGES_EDIT = 'privileges_edit', // aanpassen privileges
  PRIVILEGES_REMOVE = 'privileges_remove', // verwijderen privileges
  PRIVILEGES_VIEW = 'privileges_view', // bekijken privileges

  PRIORITY_REQUEST = 'priority_request', // aanvragen prioriteit
  PRIORITY_APPROVAL = 'priority_approval', // goedkeuren prioriteit
  PRIORITY_DENY = 'priority_deny', // afkeuren prioriteit
  PRIORITY_VIEW_ALL = 'priority_view_all', // bekijken alle prioriteiten
  PRIORITY_VIEW_PER_PARTICIPANT_AND_STATUS = 'priority_view_per_participant_and_status', // bekijken prioriteit per deelnemer + status

  CYCLE_DETERMINE_START = 'cycle:determine_start', // bepalen of cyclus gestart mag worden
  CYCLE_START = 'cycle:start', // starten cyclus

  MATCHRUN_DETERMINE_START = 'matchrun:determine_start', // bepalen of matchrun gestart mag worden
  MATCHRUN_START = 'matchrun:start', // (her)starten run
  MATCHRUN_SETTINGS = 'matchrun:settings', // access matchrun settings
  MATCHRUN_VIEW = 'matchrun:view', // bekijken matchruns en resultaten zonder startrechten
  MATCHRUN_VIEW_PAIRS_AND_PARTICIPANTS = 'matchrun:view_pairs_and_participants', // bekijken koppels / matches uit run (al dan niet geanonimiseerd)
  MATCHRUN_VIEW_HISTORY_PAIR = 'matchrun:view_history_pair', // bekijken historie koppel
  MATCHRUN_VIEW_RESULTS = 'matchrun:view_results', // bekijken vorige uitslagen
  MATCHRUN_ESTABLISH_TRANSPLANT_ON_PAIR = 'matchrun:establish_transplant_on_pair', // vastleggen transplantatie op koppel (op basis van de matchuitslag, met in ieder geval een transplantatiedatum)
  MATCHRUN_EXCLUDE_PAIR_FROM_MATCH = 'matchrun:exclude_pair_from_match', // uitsluiten deelnemers van match
  MATCHRUN_LOCK_CHAINS = 'matchrun:lock_chains', // keten (un)locken
  MATCHRUN_SELECT_CHAIN_FOR_TRANSPLANT = 'matchrun:select_chain_for_transplant', // ketens selecteren voor transplantatie
  MATCHRUN_DOWNLOAD = 'matchrun:download', // downloaden matchrun gegevens
  MATCHRUN_ADD_CROSSMATCH = 'matchrun:add_crossmatch', // toevoegen crossmatch in matchrun scherm
  MATCHRUN_ADD_COMBINATION = 'matchrun:add_combination', // toevoegen excluded combination in matchrun scherm
  MATCHRUN_VIEW_COMMENTS = 'matchrun:view_comments', // bekijken opmerkingen bij matchrun

  CROSSMATCH_RESULT_ADD = 'crossmatch:result_add', // uitslagen kruisproeven invoeren
  CROSSMATCH_RESULT_VIEW = 'crossmatch:result_view', // uistlagen kruisproeven bekijken
  CROSSMATCH_RESULT_PRINT = 'crossmatch:result_print', // uitslagen kruisproeven uitprinten / opslaan

  NEWSITEM_ADD = 'newsitem:add', // opstellen nieuwsbericht
  NEWSITEM_READ = 'newsitem:read', // lezen nieuwsbericht
  NEWSITEM_EDIT = 'newsitem:edit', // aanpassen nieuwsbericht
  NEWSITEM_DELETE = 'newsitem:delete', // verwijderen nieuwsbericht

  NOTIFICATIONS_ACTIVATE = 'notifications:activate', // activeren notificaties
  NOTIFICATIONS_INACTIVATE = 'notifications:inactivate', // inactiveren notificaties

  SWITCH_ROLE = 'switch_role', // switch'en overige rollen

  MAIL_CENTRUM = 'mail:centrum', //mail'en centra  (per chain, cycle, matchrun, selected)

  EXPORT_ANONYMOUS_DATA = 'export:anonymous_data', // exporteren geanonimiseerde gegevens

  PARTICIPANTS_PAGE = 'participants:page', // used in frontend
  PAIRS_PAGE = 'pairs:page',
  NEWSITEM_PAGE = 'newsitem:page',
  TIMELINE_PAGE = 'timeline:page',
  PARTICIPANTS_HISTORY_PAGE = 'participants:history_page',
  PARTICIPANTS_HISTORICAL_PAGE = 'participants:historical_page', // toegang tot historisch deelnemers overzicht (Operator, Allocatie, Centra)
  PAIR_HISTORY_PAGE = 'pair:history_page',
  CROSSMATCH_PAGE = 'crossmatch:page',
  APPROVAL_PAGE = 'approval:page',
  MATCHRUN_PAGE = 'matchrun:page', // used in frontend
  CENTERS_VIEW = 'centers:view', // bekijken centra lijst
  CENTERS_PAGE = 'centers:page', // used in frontend
  CENTERS_EDIT = 'centers:edit' // edit center information
}

const generalDashboardPermissions: Permission[] = [
  Permission.DASHBOARD_READ,
  Permission.DASHBOARD_CYCLE,
  Permission.DASHBOARD_MATCHRUN,
  Permission.DASHBOARD_NOTIFICATIONS,
  Permission.DASHBOARD_NEWSITEMS
];

export const Roles: { [key: string]: Role } = {
  OPERATOR: {
    name: 'Operator',
    enumName: 'OPERATOR',
    description: 'NTS Operator (allocation)',
    permissions: [
      ...generalDashboardPermissions,
      Permission.DASHBOARD_ALL_PATIENTS,
      Permission.PARTICIPANTS_ADD_INCLUDING_ALTRUISTS,
      Permission.PARTICIPANS_VIEW_WITH_WARNINGS,
      Permission.PARTICIPANTS_VIEW_ALL,
      Permission.PARTICIPANTS_VIEW_HISTORY,
      Permission.PARTICIPANTS_EDIT,
      Permission.PARTICIPANTS_SET_TRANSPLANTED,
      Permission.PARTICIPANTS_INACTIVATE,
      Permission.PARTICIPANTS_IMPORT_ENIS,
      Permission.ENISSYNC_VIEW_LOGS,
      Permission.PAIRS_LINK_PARTICIPANTS,
      Permission.PAIRS_VIEW_ALL,
      Permission.PAIRS_VIEW_HISTORY,
      Permission.PAIRS_EDIT,
      Permission.PAIRS_INACTIVATE,
      Permission.PAIRS_ADD_TO_CYCLE,
      Permission.PAIRS_REMOVE_FROM_CYCLE,
      Permission.PRIVILEGES_ADD,
      Permission.PRIVILEGES_EDIT,
      Permission.PRIVILEGES_REMOVE,
      Permission.PRIVILEGES_VIEW,
      Permission.PRIORITY_REQUEST,
      Permission.PRIORITY_APPROVAL,
      Permission.PRIORITY_DENY,
      Permission.PRIORITY_VIEW_ALL,
      Permission.PRIORITY_VIEW_PER_PARTICIPANT_AND_STATUS,
      Permission.CYCLE_DETERMINE_START,
      Permission.CYCLE_START,
      Permission.MATCHRUN_DETERMINE_START,
      Permission.MATCHRUN_START,
      Permission.MATCHRUN_VIEW,
      Permission.MATCHRUN_VIEW_PAIRS_AND_PARTICIPANTS,
      Permission.MATCHRUN_VIEW_HISTORY_PAIR,
      Permission.MATCHRUN_VIEW_RESULTS,
      Permission.MATCHRUN_ESTABLISH_TRANSPLANT_ON_PAIR,
      Permission.MATCHRUN_EXCLUDE_PAIR_FROM_MATCH,
      Permission.MATCHRUN_LOCK_CHAINS,
      Permission.MATCHRUN_SELECT_CHAIN_FOR_TRANSPLANT,
      Permission.MATCHRUN_ADD_CROSSMATCH,
      Permission.MATCHRUN_ADD_COMBINATION,
      Permission.MATCHRUN_VIEW_COMMENTS,
      Permission.CROSSMATCH_RESULT_ADD,
      Permission.CROSSMATCH_RESULT_VIEW,
      Permission.CROSSMATCH_RESULT_PRINT,
      Permission.NEWSITEM_ADD,
      Permission.NEWSITEM_READ,
      Permission.NEWSITEM_EDIT,
      Permission.NEWSITEM_DELETE,
      Permission.NOTIFICATIONS_ACTIVATE,
      Permission.NOTIFICATIONS_INACTIVATE,
      Permission.SWITCH_ROLE,
      Permission.EXPORT_ANONYMOUS_DATA,
      Permission.MATCHRUN_SETTINGS,
      Permission.MATCHRUN_DOWNLOAD,
      // Pages for frontend
      Permission.PARTICIPANTS_PAGE,
      Permission.PAIRS_PAGE,
      Permission.NEWSITEM_PAGE,
      Permission.TIMELINE_PAGE,
      Permission.PARTICIPANTS_HISTORY_PAGE,
      Permission.PARTICIPANTS_HISTORICAL_PAGE,
      Permission.PAIR_HISTORY_PAGE,
      Permission.CROSSMATCH_PAGE,
      Permission.APPROVAL_PAGE,
      Permission.MATCHRUN_PAGE,
      Permission.CENTERS_VIEW,
      Permission.CENTERS_PAGE,
      Permission.CENTERS_EDIT
    ]
  },
  HLA_LAB: {
    name: 'HLA Lab',
    enumName: 'HLA_LAB',
    description: 'HLA Lab',
    permissions: [
      ...generalDashboardPermissions,
      Permission.DASHBOARD_ALL_PATIENTS,
      Permission.MATCHRUN_VIEW,
      Permission.MATCHRUN_VIEW_PAIRS_AND_PARTICIPANTS,
      Permission.MATCHRUN_ADD_CROSSMATCH,
      Permission.CROSSMATCH_RESULT_ADD,
      Permission.CROSSMATCH_RESULT_VIEW,
      Permission.CROSSMATCH_RESULT_PRINT,
      Permission.NEWSITEM_READ,
      Permission.NOTIFICATIONS_ACTIVATE,
      Permission.NOTIFICATIONS_INACTIVATE,
      Permission.PRIVILEGES_VIEW,
      Permission.PAIRS_VIEW_ALL,
      Permission.PARTICIPANTS_VIEW_ALL,
      Permission.MATCHRUN_DOWNLOAD,
      // Pages for frontend
      Permission.NEWSITEM_PAGE,
      Permission.MATCHRUN_PAGE,
      Permission.TIMELINE_PAGE
    ]
  },
  XOCOMMISION: {
    name: 'XOCommisie',
    enumName: 'XOCOMMISION',
    description: 'NTS XO Commisie',
    permissions: [
      ...generalDashboardPermissions,
      Permission.DASHBOARD_ALL_PATIENTS,
      Permission.PARTICIPANS_VIEW_WITH_WARNINGS,
      Permission.PARTICIPANTS_VIEW_ALL,
      Permission.PAIRS_VIEW_ALL,
      Permission.PRIVILEGES_VIEW,
      Permission.PRIORITY_APPROVAL,
      Permission.PRIORITY_DENY,
      Permission.PRIORITY_VIEW_ALL,
      Permission.CYCLE_DETERMINE_START,
      Permission.MATCHRUN_DETERMINE_START,
      Permission.MATCHRUN_VIEW_PAIRS_AND_PARTICIPANTS,
      Permission.CROSSMATCH_RESULT_VIEW,
      Permission.NEWSITEM_READ,
      Permission.NOTIFICATIONS_ACTIVATE,
      Permission.NOTIFICATIONS_INACTIVATE,
      Permission.MATCHRUN_DOWNLOAD,
      // Pages for frontend
      Permission.PARTICIPANTS_PAGE,
      Permission.PAIRS_PAGE,
      Permission.APPROVAL_PAGE,
      Permission.NEWSITEM_PAGE,
      // Permission.CROSSMATCH_PAGE removed - only OPERATOR and ALLOCATION can access
      Permission.MATCHRUN_PAGE,
      Permission.TIMELINE_PAGE
    ]
  },
  ALLOCATION: {
    name: 'NTS Allocatie',
    enumName: 'ALLOCATION',
    description: 'NTS Allocatie',
    permissions: [
      ...generalDashboardPermissions,
      Permission.DASHBOARD_ALL_PATIENTS,
      Permission.PARTICIPANTS_ADD_INCLUDING_ALTRUISTS,
      Permission.PARTICIPANS_VIEW_WITH_WARNINGS,
      Permission.PARTICIPANTS_VIEW_ALL,
      Permission.PARTICIPANTS_VIEW_HISTORY,
      Permission.PARTICIPANTS_EDIT,
      Permission.PARTICIPANTS_SET_TRANSPLANTED,
      Permission.PARTICIPANTS_INACTIVATE,
      Permission.PARTICIPANTS_IMPORT_ENIS,
      Permission.PAIRS_LINK_PARTICIPANTS,
      Permission.PAIRS_VIEW_ALL,
      Permission.PAIRS_VIEW_HISTORY,
      Permission.PAIRS_EDIT,
      Permission.PAIRS_INACTIVATE,
      Permission.PAIRS_ADD_TO_CYCLE,
      Permission.PAIRS_REMOVE_FROM_CYCLE,
      Permission.PRIVILEGES_VIEW,
      Permission.PRIORITY_VIEW_PER_PARTICIPANT_AND_STATUS,
      Permission.CYCLE_START,
      Permission.MATCHRUN_START,
      Permission.MATCHRUN_VIEW,
      Permission.MATCHRUN_VIEW_PAIRS_AND_PARTICIPANTS,
      Permission.MATCHRUN_VIEW_HISTORY_PAIR,
      Permission.MATCHRUN_VIEW_RESULTS,
      Permission.MATCHRUN_EXCLUDE_PAIR_FROM_MATCH,
      Permission.MATCHRUN_LOCK_CHAINS,
      Permission.MATCHRUN_SELECT_CHAIN_FOR_TRANSPLANT,
      Permission.MATCHRUN_ADD_COMBINATION,
      Permission.CROSSMATCH_RESULT_VIEW,
      Permission.NEWSITEM_ADD,
      Permission.NEWSITEM_READ,
      Permission.NEWSITEM_EDIT,
      Permission.NEWSITEM_DELETE,
      Permission.NOTIFICATIONS_ACTIVATE,
      Permission.NOTIFICATIONS_INACTIVATE,
      Permission.MAIL_CENTRUM,
      Permission.EXPORT_ANONYMOUS_DATA,
      Permission.PRIORITY_VIEW_ALL,
      Permission.MATCHRUN_SETTINGS,
      Permission.MATCHRUN_DOWNLOAD,
      Permission.MATCHRUN_VIEW_COMMENTS,


      // TODO: Nacheken of dit mag?
            Permission.CROSSMATCH_RESULT_ADD,
      // Pages for frontend
      Permission.PARTICIPANTS_PAGE,
      Permission.PARTICIPANTS_HISTORY_PAGE,
      Permission.PARTICIPANTS_HISTORICAL_PAGE,
      Permission.PAIRS_PAGE,
      Permission.PAIR_HISTORY_PAGE,
      Permission.APPROVAL_PAGE,
      Permission.NEWSITEM_PAGE,
      Permission.CROSSMATCH_PAGE,
      Permission.MATCHRUN_PAGE,
      Permission.TIMELINE_PAGE,
      Permission.CENTERS_VIEW,
      Permission.CENTERS_PAGE,
      Permission.CENTERS_EDIT
    ]
  },
  CENTER: {
    name: 'Center',
    enumName: 'CENTER',
    description: 'Center Coordinator (hospital)',
    permissions: [
      ...generalDashboardPermissions,
      Permission.DASHBOARD_OWN_CENTER_PATIENTS,
      Permission.PARTICIPANTS_ADD_INCLUDING_ALTRUISTS,
      Permission.PARTICIPANTS_VIEW_OWN_CENTER,
      Permission.PARTICIPANS_VIEW_WITH_WARNINGS,
      Permission.PARTICIPANTS_VIEW_HISTORY,
      Permission.PARTICIPANTS_EDIT,
      Permission.PARTICIPANTS_SET_TRANSPLANTED,
      Permission.PARTICIPANTS_INACTIVATE,
      Permission.PAIRS_LINK_PARTICIPANTS,
      Permission.PAIRS_VIEW_OWN_CENTER,
      Permission.PAIRS_VIEW_HISTORY,
      Permission.PAIRS_EDIT,
      Permission.PAIRS_INACTIVATE,
      Permission.PAIRS_ADD_TO_CYCLE,
      Permission.PAIRS_REMOVE_FROM_CYCLE,
      Permission.PRIVILEGES_ADD,
      Permission.PRIVILEGES_EDIT,
      Permission.PRIVILEGES_REMOVE,
      Permission.PRIVILEGES_VIEW,
      Permission.PRIORITY_REQUEST,
      Permission.PRIORITY_VIEW_PER_PARTICIPANT_AND_STATUS,
      Permission.MATCHRUN_VIEW_PAIRS_AND_PARTICIPANTS,
      Permission.MATCHRUN_ESTABLISH_TRANSPLANT_ON_PAIR,
      Permission.CROSSMATCH_RESULT_VIEW,
      Permission.NEWSITEM_READ,
      Permission.NOTIFICATIONS_ACTIVATE,
      Permission.NOTIFICATIONS_INACTIVATE,
      Permission.PRIORITY_VIEW_ALL,

      // Pages for frontend
      Permission.PARTICIPANTS_PAGE,
      Permission.PARTICIPANTS_HISTORY_PAGE,
      Permission.PARTICIPANTS_HISTORICAL_PAGE,
      Permission.PAIRS_PAGE,
      Permission.PAIR_HISTORY_PAGE,
      Permission.APPROVAL_PAGE,
      Permission.NEWSITEM_PAGE,
      // Permission.CROSSMATCH_PAGE removed - only OPERATOR and ALLOCATION can access
      Permission.MATCHRUN_PAGE,
      Permission.TIMELINE_PAGE,
      Permission.CENTERS_VIEW
    ]
  }
};
